import _ from "lodash";
import {
  MS_FILE_VIEWER_SUPPORTED_MIMETYPE,
  GOOGLE_FILE_VIEWER_SUPPORTED_MIMETYPE,
  WORK_BOOK_URL,
} from "Constants";
import ACLStore from "lib/aclStore";
import { getFileThumbUrl, isLocalUrl } from "./AttachmentHelpers";
import { canOpenOfficeFileInPdftron } from "./PdftronViewerHelpers";
import {
  WorkbookOutlined,
  LinkOutlined,
  ZipFileColored,
  ImageSquareOutlined,
  VideoOutlined,
  VolumeHighOutlined,
  PdfOutlined,
  NoteOutlined,
  PlaceholderFileOutlined,
  AssessmentEvaluatedOutlined,
  PageSquareOutlined,
  SheetsOutlined,
  SlidesOutlined,
  GoalsOutlined,
  PageFoldPortraitOutlined,
} from "@toddle-edu/ds-icons";
import React from "react";
import { isPublicPlatformRoute } from "./RouteMatchUtils";
const googleLinksRegex = /https:\/\/docs.google.com\/(document|spreadsheets|presentation)/;

const SKIPPABLE_MS_VIEWER_MIMETYPES = _.omit(
  MS_FILE_VIEWER_SUPPORTED_MIMETYPE,
  [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ]
);

export const GetTheatreModeType = ({ media, isWorksheet = false }) => {
  const mediaType = _.get(media, "type", "");
  const mediaMimeType = _.get(media, "mimeType", "");
  const url = _.get(media, "url", "");
  const urlRegx = new RegExp(
    "(docs.google.com)(://[A-Za-z]+-my.sharepoint.com)?",
    "i"
  );

  // Regex for Google Drive files only (no folders, no docs)
  const googleDriveRegex = /https:\/\/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+/;

  const serviceType = _.get(media, "metadata.serviceType", "");
  const isLtiTool = _.isEqual(_.get(media, "metadata.type"), "LTI_TOOL");
  switch (mediaType) {
    case "WORKBOOK":
      return "WORKBOOKVIEWER";
    case "CODE_EDITOR":
      return "CODE_EDITOR_VIEWER";
    case "TODDLE_DOC":
      return "TODDLE_DOC_VIEWER";
    case "IMAGE":
      if (url?.includes(WORK_BOOK_URL)) return "FILEVIEWER";
      return "IMAGEVIEWER";

    case "FILE":
      if (_.isEqual(mediaMimeType, "application/pdf")) return "PDFTRONVIEWER";
      if (
        _.includes(GOOGLE_FILE_VIEWER_SUPPORTED_MIMETYPE, mediaMimeType) ||
        _.includes(MS_FILE_VIEWER_SUPPORTED_MIMETYPE, mediaMimeType)
      ) {
        return ACLStore.can("FeatureFlag:disableMicrosoftEmbededViewer") &&
          _.includes(SKIPPABLE_MS_VIEWER_MIMETYPES, mediaMimeType)
          ? "MEDIAPLACEHOLDER"
          : canOpenOfficeFileInPdftron()
          ? "PDFTRONVIEWER"
          : isLocalUrl(url)
          ? "MEDIAPLACEHOLDER"
          : "FILEVIEWER";
      }
      return "MEDIAPLACEHOLDER";
    case "LINK":
      if (urlRegx.test(url) || (isWorksheet && googleDriveRegex.test(url)))
        return "FILEVIEWER";
      if (
        serviceType == "MICROSOFT" &&
        ACLStore.can("FeatureFlag:MicrosoftAnyoneLinkGeneration")
      )
        return "MIRCOSOFT_URL";
      if (isLtiTool) return "LTI_TOOL";
      return "MEDIAPLACEHOLDER";
    case "QUESTION":
      return "QUESTION";
    case "QUESTION_SUBMISSION":
      return "QUESTION_SUBMISSION";
    default:
      return "MEDIAPLACEHOLDER";
  }
};

export const showNewTheatreMode = () => {
  return (
    isPublicPlatformRoute() || ACLStore.can("FeatureFlag:showTheatreModeV2")
  );
};

export const getAttachmentType = attachment => {
  let attachmentType = _.get(attachment, "type", "FILE");
  if (
    !_.size(Object.keys(attachment)) &&
    getFileThumbUrl(_.get(attachment, "mimeType") === "UNKNOWN")
  ) {
    attachmentType = "POST_RATED_LEARNING_GOALS";
  }
  if (attachmentType == "LINK") {
    const { fileType } = getFileThumbUrl(_.get(attachment, "mimeType"));
    if (googleLinksRegex.test(_.get(attachment, "url"))) {
      const attachmentUrl = _.get(attachment, "url");
      attachmentType = _.includes(attachmentUrl, "document")
        ? "DOCUMENT"
        : _.includes(attachmentUrl, "spreadsheets")
        ? "SHEET"
        : _.includes(attachmentUrl, "presentation")
        ? "SLIDES"
        : "LINK";
    } else attachmentType = fileType == "UNKNOWN" ? "LINK" : fileType;
  }
  if (attachmentType == "FILE") {
    const { fileType } = getFileThumbUrl(_.get(attachment, "mimeType"));
    if (
      fileType == "PDF" ||
      fileType == "DOCUMENT" ||
      fileType == "SHEET" ||
      fileType == "SLIDES"
    )
      attachmentType = fileType;
  }
  return attachmentType;
};

export const getIconByAttachmentType = ({
  attachment,
  fileIconSize = "x-small",
  iconVariant = "on",
}) => {
  if (!_.isUndefined(_.get(attachment, "assessmentToolType"))) {
    return (
      <AssessmentEvaluatedOutlined variant={iconVariant} size={fileIconSize} />
    );
  }
  const attachmentType = getAttachmentType(attachment);
  let icon = (
    <PlaceholderFileOutlined variant={iconVariant} size={fileIconSize} />
  );
  switch (attachmentType) {
    case "IMAGE":
      icon = <ImageSquareOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "VIDEO":
      icon = <VideoOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "AUDIO":
      icon = <VolumeHighOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "PDF":
      icon = <PdfOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "DOCUMENT":
      icon = <PageSquareOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "SHEET":
      icon = <SheetsOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "SLIDES":
      icon = <SlidesOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "ZIP":
      icon = <ZipFileColored variant={iconVariant} size={fileIconSize} />;
      break;
    case "WORKBOOK":
      icon = <WorkbookOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "LINK":
      icon = <LinkOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "NOTE":
      icon = <NoteOutlined variant={iconVariant} size={fileIconSize} />;
      break;
    case "TODDLE_DOC":
      icon = (
        <PageFoldPortraitOutlined variant={iconVariant} size={fileIconSize} />
      );
      break;
    case "UNKNOWN":
      icon = (
        <PlaceholderFileOutlined variant={iconVariant} size={fileIconSize} />
      );
      break;
    case "POST_RATED_LEARNING_GOALS":
      icon = <GoalsOutlined variant={iconVariant} size={fileIconSize} />;
  }
  return icon;
};
